import { Button as ShadcnButton, buttonVariants } from '@/components/ui/button';
import * as LucideIcons from 'lucide-react';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/lib/analytics';

export interface ButtonProps {
	id: string;
	label?: string | null;
	variant?: string | null;
	url?: string | null;
	type?: 'page' | 'post' | 'url' | 'submit' | null;
	page?: { permalink: string | null };
	post?: { slug: string | null };
	size?: 'default' | 'sm' | 'lg' | 'icon';
	icon?: string | null;
	customIcon?: any;
	icon_position?: 'left' | 'right' | null;
	className?: string;
	onClick?: () => void;
	disabled?: boolean;
	block?: boolean;
	buttonTheme?: 'light' | 'orange' | 'blue' | 'dark' | 'seaweed';
}

const Button = ({
	id,
	label,
	variant,
	url,
	type,
	page,
	post,
	size = 'default',
	icon,
	customIcon,
	icon_position = 'left',
	className,
	onClick,
	disabled = false,
	block = false,
	buttonTheme = 'light',
}: ButtonProps) => {
	// Resolve icon component
	// Prioritize customIcon (passed as prop), then dynamic lookup from string
	// Fallback to null if icon string is not found in LucideIcons
	let IconComponent = customIcon;

	if (!IconComponent && icon && typeof icon === 'string') {
		// Normalize kebab-case (e.g. "arrow-right") to PascalCase (e.g. "ArrowRight")
		// Lucide icons are exported as PascalCase
		const toPascalCase = (str: string) => str.replace(/(^\w|-\w)/g, (clear) => clear.replace(/-/, '').toUpperCase());

		const iconName = toPascalCase(icon);
		IconComponent = (LucideIcons as any)[iconName];

		// Try exact match if normalization didn't work (e.g. user already entered PascalCase)
		if (!IconComponent) {
			IconComponent = (LucideIcons as any)[icon];
		}

		if (!IconComponent) {
			console.warn(`Icon "${icon}" (normalized: "${iconName}") not found in Lucide icons.`);
		}
	}
	const position = icon_position || 'left';

	const href = (() => {
		if (type === 'page' && page?.permalink) return page.permalink;
		if (type === 'post' && post?.slug) return `/advies/${post.slug}`;

		return url || undefined;
	})();

	const theme = buttonTheme || 'light';
	const safeVariant = variant || 'default';

	// Map 'solid' to 'default' as they are the same in our design system
	const effectiveVariant = safeVariant === 'solid' ? 'default' : safeVariant;

	// Helper to get theming classes that will OVERRIDE the shadcn variant
	const getThemeOverride = (v: string, t: string) => {
		if (t === 'light') {
			if (v === 'outline') return 'border-gray-500 text-gray-900 hover:bg-gray-100 hover:text-gray-900';

			return '';
		}

		if (t === 'orange') {
			if (v === 'default') return 'bg-white text-orange hover:bg-gray-100 hover:text-orange';
			if (v === 'outline') return 'border-white text-white hover:bg-white hover:text-orange';
			if (v === 'soft') return 'bg-white/20 text-white hover:bg-white/30 hover:text-white';
			if (v === 'ghost') return 'bg-transparent text-white hover:bg-white/10 hover:text-white';
			if (v === 'link') return 'text-white hover:text-white/80 decoration-white';
		}
		if (t === 'blue' || t === 'seaweed') {
			// Breuer blue / seaweed. We keep default (orange) for high CTA contrast!
			if (v === 'default') return 'bg-orange text-white hover:bg-orange-hover hover:text-white';
			if (v === 'outline')
				return `border-white text-white hover:bg-white ${t === 'blue' ? 'hover:text-blue' : 'hover:text-seaweed'}`;
			if (v === 'soft') return 'bg-white/10 text-white hover:bg-white/20 hover:text-white';
			if (v === 'ghost') return 'bg-transparent text-white hover:bg-white/10 hover:text-white';
			if (v === 'link') return 'text-white hover:text-white/80 decoration-white';
		}
		if (t === 'dark') {
			if (v === 'default') return 'bg-orange text-white hover:bg-orange-hover hover:text-white';
			if (v === 'outline') return 'border-white text-white hover:bg-white hover:text-black';
			if (v === 'soft') return 'bg-white/10 text-white hover:bg-white/20 hover:text-white';
			if (v === 'ghost') return 'bg-transparent text-white hover:bg-white/10 hover:text-white';
			if (v === 'link') return 'text-white hover:text-white/80 decoration-white';
		}

		return '';
	};

	const themeOverride = getThemeOverride(effectiveVariant, theme);

	const buttonClasses = cn(
		buttonVariants({ variant: effectiveVariant as any, size }),
		themeOverride,
		className,
		disabled && 'opacity-50 cursor-not-allowed',
		block && 'w-full',
	);

	const content = (
		<span className="flex items-center gap-2">
			{position === 'left' && IconComponent && <IconComponent className="size-4 shrink-0" />}
			{label && <span>{label}</span>}
			{position === 'right' && IconComponent && <IconComponent className="size-4 shrink-0" />}
		</span>
	);

	if (href) {
		const handleCtaClick = () => {
			trackEvent('cta_click', {
				button_label: label || '',
				button_url: href,
				button_variant: effectiveVariant,
			});
		};

		return (
			<ShadcnButton asChild variant={effectiveVariant as any} size={size} className={buttonClasses} disabled={disabled}>
				{href.startsWith('/') || href.startsWith('#') ? (
					<Link href={href} scroll={true} onClick={handleCtaClick}>{content}</Link>
				) : (
					<a href={href} target="_blank" rel="noopener noreferrer" onClick={handleCtaClick}>
						{content}
					</a>
				)}
			</ShadcnButton>
		);
	}

	return (
		<ShadcnButton
			variant={effectiveVariant as any}
			size={size}
			className={buttonClasses}
			onClick={onClick}
			disabled={disabled}
		>
			{content}
		</ShadcnButton>
	);
};

export default Button;
